import { Controller } from "@hotwired/stimulus"

// Manages select-all / deselect-all toggles for groups of checkboxes.
// Optionally shows a contextual selection bar with the live count.
//
// One of three selection engines, kept deliberately (decision-46). This is the
// TABLE engine: its select-all belongs in the `<thead>`, over the column it
// ticks, and it tolerates an inner per-group engine (`group-select`) reporting
// up through bubbled `change` events, which a single nested Stimulus scope
// cannot do. What it does NOT own is the ANATOMY — the bar it raises is the
// same object `AppUi::SelectableList` raises, down to the sentence below.
//
// Usage:
//   <div data-controller="checkbox-select-all"
//        data-checkbox-select-all-noun-value="project">
//     <input type="checkbox" data-checkbox-select-all-target="selectAll"
//            data-action="change->checkbox-select-all#toggleAll">
//     <input type="checkbox" data-checkbox-select-all-target="checkbox"
//            data-action="change->checkbox-select-all#updateSelectAll">
//
//     <!-- Optional contextual selection bar -->
//     <div data-checkbox-select-all-target="bulkBar" class="hidden">
//       <span data-checkbox-select-all-target="count"></span>
//       <button data-action="click->checkbox-select-all#clearAll">Clear</button>
//     </div>
//   </div>
//
export default class extends Controller {
  static targets = [
    "selectAll", "checkbox", "bulkBar", "count", "exportButton",
    // Select-all-across-pages (TASK-255) — mirrors form-picker's targets of
    // the same name.
    "scopeInput", "scopeOffer"
  ]
  static values = {
    exportUrl: String,
    // Singular noun the count pluralises — "project", "submission". Mirrors
    // `form-picker`'s value of the same name so both engines write the same
    // sentence: "3 projects selected", never "3 of 6 row(s) selected."
    noun: { type: String, default: "row" },
    // Select-all-across-pages (TASK-255, mirrors form-picker's value of the
    // same name): the true count of the whole filtered relation, which the
    // DOM's own checkboxes can't tell us on a paginated list — only the
    // server, at render time, knows it.
    allInScopeCount: { type: Number, default: 0 }
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this._offered().forEach(checkbox => {
      checkbox.checked = checked
    })
    this._updateBulkBar()
  }

  updateSelectAll() {
    if (this.hasSelectAllTarget) {
      const offered = this._offered()
      const checkedCount = offered.filter(cb => cb.checked).length

      this.selectAllTarget.checked = offered.length > 0 && checkedCount === offered.length
      this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < offered.length
    }
    this._updateBulkBar()
  }

  // Unlike toggleAll this reaches EVERY checkbox, on screen or not: a twin
  // left ticked behind a breakpoint would post its id again on the next bulk
  // action. `form-picker#clear` draws the same line for search-hidden rows.
  clearAll() {
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.checkboxTargets.forEach(checkbox => {
      if (!checkbox.checked) return
      checkbox.checked = false
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    })
    this.clearScope()
    this._updateBulkBar()
  }

  // The bar's "Select all N that <filter>" link (TASK-255, porting
  // form-picker's selectAllInScope onto the table engine): ticks this
  // page's own boxes (so the count looks right while JS is in control) and
  // flags the hidden `select_scope` field so a bulk action button's
  // `injectSelection` posts the whole filtered relation instead of
  // `row_ids[]`.
  selectAllInScope(event) {
    event.preventDefault()
    this._offered().forEach((checkbox) => {
      checkbox.checked = true
    })
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    }
    if (this.hasScopeInputTarget) this.scopeInputTarget.value = "filter"
    this.scopeOfferTargets.forEach((el) => (el.hidden = true))
    this._updateBulkBar()
  }

  clearScope() {
    if (this.hasScopeInputTarget) this.scopeInputTarget.value = ""
    this.scopeOfferTargets.forEach((el) => (el.hidden = false))
  }

  inScope() {
    return this.hasScopeInputTarget && this.scopeInputTarget.value === "filter"
  }

  // A bulk action button with no confirmation dialog has no form of its own
  // carrying the ticked ids — `button_to` gives it an isolated `<form>`,
  // and this list's own checkboxes can't nest inside it (nested forms are
  // invalid HTML). Wired via `data-action="click->checkbox-select-all#
  // injectSelection"` on the button (TASK-255): copies either a single
  // `select_scope=filter` (scope mode is on) or every ticked checkbox's
  // name/value into that form as hidden fields before it submits — the
  // same thing `Common::Confirmation`'s `performConfirmation()` already
  // does for a CONFIRMED bulk action, minus the modal.
  injectSelection(event) {
    const form = event.currentTarget.closest("form")
    if (!form) return

    if (this.inScope()) {
      form.appendChild(this._hiddenInput("select_scope", "filter"))
      return
    }

    this._offered()
      .filter((cb) => cb.checked)
      .forEach((cb) => form.appendChild(this._hiddenInput(cb.name, cb.value)))
  }

  // --- Private ---

  _hiddenInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  // The checkboxes this list is actually offering the reader. A DataList
  // renders BOTH a desktop table row and a mobile card for every record, each
  // carrying a checkbox target, so counting every target counts every record
  // twice — the bar read "4 of 4 row(s) selected." on a two-row table, and a
  // select-all posted each id twice. Only one of a twinned pair is ever on
  // screen, so visibility is the discriminator (`form-picker#toggleAll` uses
  // the same test to leave search-hidden rows alone). Lists without twins —
  // the submissions inbox — are unaffected: every row is visible.
  //
  // A `:disabled` checkbox (DESIGN.md "disabled but visible", TASK-256) is
  // excluded too — same rule `form-picker` already applies: a greyed box
  // beside the reason it can't be picked counts for nothing, and select-all
  // never reaches it.
  _offered() {
    return this.checkboxTargets.filter(cb => cb.offsetParent !== null && !cb.disabled)
  }

  _updateBulkBar() {
    if (!this.hasBulkBarTarget) return

    const checkedCount = this._offered().filter(cb => cb.checked).length
    const allInScope = this.inScope()

    if (this.hasCountTarget) {
      const plural = `${this.nounValue}s`
      this.countTarget.textContent = allInScope
        ? `All ${this.allInScopeCountValue} ${this.allInScopeCountValue === 1 ? this.nounValue : plural} selected`
        : checkedCount === 0
          ? `No ${plural} selected`
          : `${checkedCount} ${checkedCount === 1 ? this.nounValue : plural} selected`
    }

    this.bulkBarTarget.classList.toggle("hidden", checkedCount === 0 && !allInScope)

    this._syncExportButton()
  }

  // The export modal needs to know the selection, and the modal controller
  // reads its src off the clicked element at click time — so keep that
  // attribute in step with the checkboxes rather than inventing a second
  // way to open a modal.
  _syncExportButton() {
    if (!this.hasExportButtonTarget || !this.hasExportUrlValue) return

    const query = this._offered()
      .filter(cb => cb.checked)
      .map(cb => `submission_ids[]=${encodeURIComponent(cb.value)}`)
      .join("&")

    this.exportButtonTarget.setAttribute(
      "data-modal-turbo-frame-src-value",
      `${this.exportUrlValue}?${query}`
    )
  }
}
